import { t } from 'i18next';
import { cn } from '../../../lib/styles';
import { Heading } from '../heading/heading.spark';
import { Icon } from '../icon/icon.spark';
import type { IconName } from '../icon/types';
import type { AlertProps, AlertVariant } from './types';

const ICON_NAMES: Record<AlertVariant, IconName> = {
  error: 'xCircle',
  warning: 'alertTriangle',
  info: 'infoCircle',
  success: 'checkCircle',
} as const;

export function Alert({ variant = 'info', ...props }: AlertProps) {
  const headerStyle = cn(
    'flex px-3 py-2 text-spark-text-inverse',
    props.title ? 'rounded-t-lg' : 'rounded-s-lg',
    {
      'bg-spark-error-solid': variant === 'error',
      'bg-spark-warning-solid': variant === 'warning',
      'bg-spark-info-solid': variant === 'info',
      'bg-spark-success-solid': variant === 'success',
    },
  );

  const bodyStyle = cn(
    'flex flex-1 items-center overflow-hidden border p-3',
    props.title ? 'rounded-b-lg' : 'rounded-e-lg',
    {
      'border-spark-error-border bg-spark-error-soft text-spark-error-soft-text':
        variant === 'error',
      'border-spark-warning-border bg-spark-warning-soft text-spark-warning-soft-text':
        variant === 'warning',
      'border-spark-info-border bg-spark-info-soft text-spark-info-soft-text': variant === 'info',
      'border-spark-success-border bg-spark-success-soft text-spark-success-soft-text':
        variant === 'success',
    },
  );

  const closeButtonStyle = cn(
    'cursor-pointer rounded-md p-0.5 outline-hidden focus:ring-2 focus:ring-offset-2',
    {
      'ring-spark-error-ring ring-offset-spark-error-solid hover:bg-spark-error-solid-hover':
        variant === 'error',
      'ring-spark-warning-ring ring-offset-spark-warning-solid hover:bg-spark-warning-solid-hover':
        variant === 'warning',
      'ring-spark-info-ring ring-offset-spark-info-solid hover:bg-spark-info-solid-hover':
        variant === 'info',
      'ring-spark-success-ring ring-offset-spark-success-solid hover:bg-spark-success-solid-hover':
        variant === 'success',
    },
  );

  // Alert will interrupt screen reader to announce. Status will wait for pause in screen reader
  const ariaRole = variant === 'error' ? 'alert' : 'status';

  return (
    <div
      role={ariaRole}
      className={cn('flex w-full rounded-lg text-sm shadow-sm', { 'flex-col': props.title })}
    >
      <div className={cn(headerStyle, 'flex items-center justify-between')}>
        <Heading
          icon={ICON_NAMES[variant]}
          iconSize="lg"
          level={props.headingLevel}
          style="sm"
          colour="white"
          weight="normal"
        >
          {props.title}
        </Heading>
        {props.title && props.onDismiss && (
          <button
            type="button"
            className={closeButtonStyle}
            onClick={props.onDismiss}
            aria-label={t('aria:dismiss')}
          >
            <Icon name="x" />
          </button>
        )}
      </div>
      <div className={bodyStyle}>{props.children}</div>
    </div>
  );
}
